import React from "react";
import { CheckCircle2, ArrowRight, Phone, Star, Zap, Crown, Rocket, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useDemoPopup } from "@/pages/LandingPage";

const packs = [
  {
    name: "Starter",
    icon: Zap,
    tagline: "Arrêtez l'hémorragie immédiatement",
    highlight: false,
    features: [
      "IA réponse appels 24/7",
      "Rappel automatique appels manqués (SMS)",
      "Notifications WhatsApp temps réel",
      "Prise de RDV simple",
    ],
  },
  {
    name: "Pro",
    icon: Star,
    tagline: "Meilleur équilibre rentabilité / tranquillité",
    highlight: true,
    badge: "Bestseller",
    features: [
      "Tout le Starter, plus :",
      "IA qualification complète",
      "WhatsApp Automation client (pack complet)",
      "Relance devis SMS + WhatsApp",
      "Agenda intelligent métier",
      "CRM Artisan solo",
      "Rapport mensuel",
    ],
  },
  {
    name: "Premium",
    icon: Crown,
    tagline: "Pour structures de 3 à 10 techniciens",
    highlight: false,
    features: [
      "Tout le Pro, plus :",
      "Multi-lignes (3 à 10 techniciens)",
      "Dispatch équipe automatique",
      "CRM équipe (jusqu'à 5 utilisateurs)",
      "Dashboard live",
      "Support prioritaire",
    ],
  },
  {
    name: "Croissance",
    icon: Rocket,
    tagline: "Pour artisans qui veulent scaler activement",
    highlight: false,
    features: [
      "Tout le Premium, plus :",
      "Google Ads OU Facebook Ads (gestion incluse)",
      "Landing page urgence dédiée",
      "Tracking appels",
      "Appel stratégie mensuel 30 min",
    ],
  },
  {
    name: "Domination",
    icon: Shield,
    tagline: "Verrouillez votre zone",
    highlight: false,
    features: [
      "Tout le Croissance, plus :",
      "Google Ads + Facebook Ads multi-villes",
      "Site web vitrine optimisé",
      "SEO local",
      "Reporting complet mensuel",
    ],
  },
];

export const UseCases = () => {
  const { openDemoPopup } = useDemoPopup();

  return (
    <section id="use-cases" className="py-14 lg:py-28 bg-slate-900">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10 lg:mb-16">
          <p className="section-label text-accent mb-4">Nos packs</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-6 tracking-tight">
            Une offre adaptée à{" "}
            <span className="text-accent">chaque étape</span>
          </h2>
          <p className="text-base lg:text-lg text-slate-400 max-w-2xl mx-auto">
            Du solo qui démarre à l'entreprise qui veut dominer sa zone — choisissez le pack qui correspond à votre ambition.
          </p>
        </div>

        {/* Packs Grid — 3 top + 2 bottom centered */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mb-5">
          {packs.slice(0, 3).map((pack, index) => (
            <PackCard key={pack.name} pack={pack} index={index} />
          ))}
        </div>
        <div className="grid md:grid-cols-2 gap-5 max-w-4xl mx-auto">
          {packs.slice(3).map((pack, index) => (
            <PackCard key={pack.name} pack={pack} index={index + 3} />
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <p className="text-slate-400 mb-5 text-sm">
            Tous les packs sont personnalisables. Option intégration CRM externe disponible.
          </p>
          <Button
            onClick={openDemoPopup}
            data-testid="packs-cta"
            className="bg-accent hover:bg-orange-600 text-white shadow-lg font-bold text-base px-8 py-5 rounded-md transition-all hover:scale-105"
          >
            <Phone className="mr-2 w-5 h-5" />
            Découvrir le pack qui me convient
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </div>
    </section>
  );
};

function PackCard({ pack, index }) {
  const Icon = pack.icon;
  const isHighlight = pack.highlight;

  return (
    <div
      className={`relative rounded-md p-6 transition-all duration-300 ${
        isHighlight
          ? "bg-gradient-to-b from-accent/20 to-accent/5 border-2 border-accent shadow-lg shadow-accent/10"
          : "bg-slate-800/60 border border-slate-700 hover:border-slate-500"
      }`}
      data-testid={`pack-card-${index}`}
    >
      {pack.badge && (
        <span className="absolute -top-3 right-4 bg-accent text-white text-xs font-bold px-3 py-1 rounded-md shadow">
          {pack.badge}
        </span>
      )}

      <div className="flex items-center gap-3 mb-4">
        <div className={`w-10 h-10 rounded-md flex items-center justify-center ${
          isHighlight ? "bg-accent/20" : "bg-slate-700"
        }`}>
          <Icon className={`w-5 h-5 ${isHighlight ? "text-accent" : "text-slate-300"}`} />
        </div>
        <div>
          <h3 className={`text-lg font-bold ${isHighlight ? "text-white" : "text-white"}`}>
            {pack.name}
          </h3>
          <p className="text-xs text-slate-400">{pack.tagline}</p>
        </div>
      </div>

      <ul className="space-y-2">
        {pack.features.map((feature, i) => {
          const isParent = feature.endsWith(":");
          return (
            <li key={i} className="flex items-start gap-2">
              {isParent ? (
                <span className="text-xs text-slate-500 font-semibold uppercase tracking-wider pt-1">{feature}</span>
              ) : (
                <>
                  <CheckCircle2 className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isHighlight ? "text-accent" : "text-primary"}`} />
                  <span className="text-sm text-slate-300">{feature}</span>
                </>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default UseCases;
